import { useState } from 'react';
import { Button, Callout, Screen } from '../../components/ui.js';
import { errorMessage } from '../../lib/api.js';
import { DeliveryLabelsDialog } from './delivery-labels.js';
import type { DeliveryLabelLine } from './delivery-labels.js';
import { useReceiptPrinting } from '../sales/receipt.js';
import type { LabelSize } from './label-geometry.js';
import { useLabelPrinter } from './labels.js';
import { useReceivingDocumentPrinter } from './receiving-document.js';

/**
 * The delivery is in: labels next, and the paper for the folder.
 *
 * Shared by the bench that counts against an order and the one that counts
 * without (SCOPE §6.1) — the end of receiving is the same either way.
 */
export function DeliveryReceived({
  receivingOrderId,
  number,
  units,
  labelLines,
  onDone,
}: {
  receivingOrderId: string;
  number: string;
  units: number;
  /** Each size that came in, laid out as grids to label (FB-1113). */
  labelLines: readonly DeliveryLabelLine[];
  onDone: () => void;
}) {
  const [labelsOpen, setLabelsOpen] = useState(false);
  const settings = useReceiptPrinting();
  const labels = useLabelPrinter();
  const receivingDoc = useReceivingDocumentPrinter(settings);

  return (
    <Screen
      title="Delivery received"
      destructiveAction={
        <Button variant="ghost" onClick={onDone}>
          Back to purchase orders
        </Button>
      }
      secondaryActions={
        /*
         * The paper trail (§6.1). Secondary rather than primary because the
         * labels are what stops the delivery going on the shelf unscannable,
         * and there is exactly one filled primary per screen (§2.1).
         */
        <Button
          variant="secondary"
          busy={receivingDoc.busy}
          onClick={() => receivingDoc.print(receivingOrderId)}
        >
          Print receiving document
        </Button>
      }
      primaryAction={
        <Button
          variant="primary"
          size="large"
          disabledReason={
            settings === null
              ? 'Loading label settings'
              : labelLines.length === 0
                ? 'Nothing to label'
                : undefined
          }
          onClick={() => setLabelsOpen(true)}
        >
          Print labels
        </Button>
      }
    >
      {labels.frame}
      {receivingDoc.frame}
      <Callout tone="success" title={`${number} — ${units} units received`}>
        Stock is on the shelf and costs have been updated. Labels next, and the receiving document
        for the folder.
      </Callout>
      {receivingDoc.error !== null ? (
        <Callout tone="danger">{errorMessage(receivingDoc.error)}</Callout>
      ) : null}

      {labelsOpen && settings !== null ? (
        <DeliveryLabelsDialog
          lines={labelLines}
          size={settings.label.size as LabelSize}
          showPrice={settings.label.showPrice}
          showBothPrices={settings.label.showBothPrices}
          printer={labels}
          onClose={() => setLabelsOpen(false)}
        />
      ) : null}
    </Screen>
  );
}
